import { createSelector } from '@reduxjs/toolkit';
import { getTypeCounts } from 'tracker/counters';
import {
  CTF_MODULE_TO_DIFFERENCE_MODULE,
  CTFModuleTypes,
  Difference,
  DifferenceTypeNames,
  SortDifferencesOption,
  StoreState,
} from 'types';
import { getCommentedDifferences, getDifferences } from '../differences/selectors';
import { groupDifferencesByGroupOrWord, isDifferenceFilteredOut } from './groupingUtils';
import { InspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { getSelectedModule } from 'store/app/appSelectors';
import { computeSubgroupIndices, computeGroupIndices, fromVerifyDifference } from '@verify/shared-utils';
import type { DifferenceNumbering, SubgroupedDifferences } from '@verify/shared-utils';

// placed inside inspection selectors to decouple filter from differences
export const getDifferenceTypeFilters = (state: StoreState) =>
  state.inspection.differenceViewOptions.differenceTypeFilters;
export const getDifferenceStatusFilters = (state: StoreState) =>
  state.inspection.differenceViewOptions.differenceStatusFilters;
export const getStoredDifferenceSortOption = (state: StoreState) => state.inspection.differenceViewOptions.sortBy;
export const getSortByModule = (state: StoreState) => state.inspection.differenceViewOptions.sortByPerModule;

/**
 * Returns the effective sort option for the currently selected module.
 *
 * Priority: per-module override > inspection-wide sortBy > readingOrder default.
 * For the All tab: always returns the inspection-wide sortBy (per-module values are ignored).
 * Guards: alphabetical is Spelling-only; fontSize is FontMetrics-only — both fall back to
 * readingOrder if they somehow appear on another module.
 */
export const getDifferenceSortOption = (state: StoreState): SortDifferencesOption => {
  const { sortBy, sortByPerModule } = state.inspection.differenceViewOptions;
  const selectedModule = getSelectedModule(state);

  if (selectedModule === 'All') {
    return sortBy ?? SortDifferencesOption.readingOrder;
  }

  const effectiveSortBy =
    sortByPerModule?.[CTF_MODULE_TO_DIFFERENCE_MODULE[selectedModule]] ?? sortBy ?? SortDifferencesOption.readingOrder;

  if (effectiveSortBy === SortDifferencesOption.alphabetical && selectedModule !== CTFModuleTypes.spelling) {
    return SortDifferencesOption.readingOrder;
  }

  if (effectiveSortBy === SortDifferencesOption.fontSize && selectedModule !== CTFModuleTypes.fontMetrics) {
    return SortDifferencesOption.readingOrder;
  }

  return effectiveSortBy;
};
export const getSelectedBarcodeOption = (state: StoreState) => state.inspection.settings.barcode;
// Making this a Boolean so that it doesn't reset & re-trigger the No-Braille popup if a user changes the braille language
// but navigates back to Result tab without re-running the inspection
export const getIsBraille = (state: StoreState) => Boolean(state.inspection.settings.brailleLanguage);

// TODO: Repurpose this function to return source or target based on difference type
const isSourceOrTarget = (difference: Difference) => {
  return difference.source?.location.rect ? 'source' : 'target';
};

export const sortDifferences = (
  differences: Difference[],
  sortOption: SortDifferencesOption | undefined,
  inspectionCustomStatuses: InspectionCustomStatuses | undefined | null,
) => {
  if (differences.length < 2) {
    return differences;
  }

  const sortedDifferences = differences.sort((a, b) => {
    // Resync differences always stay at the top (before Markup priority)
    const aHasResyncId = a.resyncId != null;
    const bHasResyncId = b.resyncId != null;
    if (aHasResyncId && !bHasResyncId) return -1;
    if (!aHasResyncId && bHasResyncId) return 1;
    // If both have resyncId, sort by resyncId descending (highest first)
    if (aHasResyncId && bHasResyncId) {
      const resyncIdA = a.resyncId ?? 0;
      const resyncIdB = b.resyncId ?? 0;
      if (resyncIdA > resyncIdB) return -1;
      if (resyncIdA < resyncIdB) return 1;
    }

    // Always prioritize Markup differences before other types
    if (a.type === 'Markup' && b.type !== 'Markup') return -1;
    if (b.type === 'Markup' && a.type !== 'Markup') return 1;

    switch (sortOption) {
      case SortDifferencesOption.differenceStatus: {
        const statusTitleA = a.statusId ? inspectionCustomStatuses?.allStatuses[a.statusId]?.title : undefined;
        const statusTitleB = b.statusId ? inspectionCustomStatuses?.allStatuses[b.statusId]?.title : undefined;
        // Move undefined/unassigned to the bottom
        if (statusTitleA === undefined && statusTitleB !== undefined) return 1;
        if (statusTitleB === undefined && statusTitleA !== undefined) return -1;

        if (statusTitleA && statusTitleB) {
          const titleA = statusTitleA.toLowerCase();
          const titleB = statusTitleB.toLowerCase();
          return titleA < titleB ? -1 : titleA > titleB ? 1 : 0;
        }
        break;
      }

      case SortDifferencesOption.differenceType:
        if (a.displayName < b.displayName) return -1;
        if (a.displayName > b.displayName) return 1;
        break;

      case SortDifferencesOption.fontSize:
        const fontSizeA = a.fontMetricsDetails?.fontPointSize ?? 0;
        const fontSizeB = b.fontMetricsDetails?.fontPointSize ?? 0;

        if (fontSizeA > fontSizeB) return 1;
        if (fontSizeA < fontSizeB) return -1;
        break;

      case SortDifferencesOption.alphabetical: {
        // Sort by `word` (the spelling-grouping key); fall back to source text otherwise.
        const wordA = (a.word ?? a.source?.text ?? '').toLocaleLowerCase();
        const wordB = (b.word ?? b.source?.text ?? '').toLocaleLowerCase();
        const compare = wordA.localeCompare(wordB);
        if (compare !== 0) return compare;
        break;
      }
    }

    // Sort by page
    if (a[isSourceOrTarget(a)].location.pageNumber < b[isSourceOrTarget(b)].location.pageNumber) {
      return -1;
    }
    if (a[isSourceOrTarget(a)].location.pageNumber > b[isSourceOrTarget(b)].location.pageNumber) {
      return 1;
    }

    // Sort by Y-coordinate
    if (a[isSourceOrTarget(a)].location.rect[1] < b[isSourceOrTarget(b)].location.rect[1]) {
      return -1;
    }
    if (a[isSourceOrTarget(a)].location.rect[1] > b[isSourceOrTarget(b)].location.rect[1]) {
      return 1;
    }

    // Sort by X-coordinate
    if (a[isSourceOrTarget(a)].location.rect[0] < b[isSourceOrTarget(b)].location.rect[0]) {
      return -1;
    }
    if (a[isSourceOrTarget(a)].location.rect[0] > b[isSourceOrTarget(b)].location.rect[0]) {
      return 1;
    }

    return 0;
  });

  return sortedDifferences;
};

// done like this to avoid cyclic deps with actions
export const isFilteredOut = isDifferenceFilteredOut;

// differences that HAVE NOT been filtered out (type or status checkbox is CHECKED)
export const getUnfilteredDifferences = createSelector(
  [getDifferences, getDifferenceTypeFilters, getDifferenceStatusFilters],
  (differences, typeFilters, statusFilters) =>
    differences.filter((diff) => !isFilteredOut(diff, typeFilters, statusFilters)),
);

// The ids of the unfiltered differences, memoized so switching module tabs (which doesn't change
// the unfiltered set) reuses the same Set instead of rebuilding it. Used for visibility checks.
export const getUnfilteredDifferenceIds = createSelector(
  [getUnfilteredDifferences],
  (unfiltered) => new Set(unfiltered.map((diff) => diff.id)),
);

// differences that HAVE been filtered out (type or status checkbox is UNCHECKED)
export const getFilteredDifferences = createSelector(
  [getDifferences, getDifferenceTypeFilters, getDifferenceStatusFilters],
  (differences, typeFilters, statusFilters) =>
    differences.filter((diff) => isFilteredOut(diff, typeFilters, statusFilters)),
);

export const getFilteredAndUnfilteredDifferences = createSelector(
  [getDifferences, getDifferenceTypeFilters, getDifferenceStatusFilters],
  (differences, typeFilters, statusFilters) =>
    differences.reduce(
      (acc, diff) => {
        if (isFilteredOut(diff, typeFilters, statusFilters)) {
          acc.filtered.push(diff);
        } else {
          acc.unfiltered.push(diff);
        }
        return acc;
      },
      { filtered: [], unfiltered: [] } as { filtered: Difference[]; unfiltered: Difference[] },
    ),
);

/**
 * Groups the differences and numbers each member within its group (subgroupIndex).
 * Memoized on the loaded set, not on filters: filtering keeps or drops a whole group at
 * once, so it never changes subgroupIndex. Reruns only when the set changes (new inspection,
 * resync, undo).
 */
export const getSubgroupedDifferences = createSelector(
  [getDifferences],
  (differences): SubgroupedDifferences<Difference> => computeSubgroupIndices(differences, fromVerifyDifference),
);

/**
 * Sorts and numbers one module's cards. The recomputeDifferenceNumbering saga calls this to build
 * the visible list in differenceListStore. Keeps only the module's visible groups, then sorts and
 * numbers them. Just one module is passed in because groupIndex restarts at 1 per module.
 *
 * statusTitlesById is optional (status titles live in React Query, not Redux); the saga passes it so
 * status-sort order is right. Returns every member with its numbering; callers pick representatives.
 */
export function computeNumberedModuleDifferences(
  subgrouped: SubgroupedDifferences<Difference>,
  unfilteredDifferences: Difference[],
  module: string,
  sortOption: SortDifferencesOption,
  statusTitlesById?: Record<string, string>,
): Array<Difference & DifferenceNumbering> {
  const unfilteredIds = new Set(unfilteredDifferences.map((d) => d.id));
  const moduleKey = CTF_MODULE_TO_DIFFERENCE_MODULE[module as CTFModuleTypes];
  const moduleGroups = (subgrouped.moduleUnits.get(moduleKey) ?? []).filter((g) =>
    unfilteredIds.has(g.representative.item.id),
  );
  const singleModuleSubgrouped = {
    moduleUnits: new Map([[moduleKey, moduleGroups]]),
  } as SubgroupedDifferences<Difference>;

  return computeGroupIndices(singleModuleSubgrouped, {
    sortOptions: { sortBy: sortOption },
    statusTitlesById,
  }) as Array<Difference & DifferenceNumbering>;
}

/**
 * Returns the current module's visible cards (one representative per group).
 *
 * On purpose, this does NOT sort or number the cards. Callers only need the set of cards — a
 * count, an id-membership check, or a selection set — not their order. The sorted, numbered list
 * is built once by the recomputeDifferenceNumbering saga into differenceListStore, so sorting here
 * would just repeat that work on every change.
 *
 * All tab: legacy groupDifferencesByGroupOrWord path.
 * TODO: remove the All-tab branch when the showAllResultsTab flag is retired.
 */
export const getDisplayedDifferenceGroups = createSelector(
  [getSubgroupedDifferences, getUnfilteredDifferences, getUnfilteredDifferenceIds, getSelectedModule],
  (subgrouped, unfiltered, unfilteredIds, module): Array<Difference & Partial<DifferenceNumbering>> => {
    if (module === 'All') {
      // TODO: remove when showAllResultsTab flag is retired.
      return groupDifferencesByGroupOrWord(unfiltered);
    }

    const moduleKey = CTF_MODULE_TO_DIFFERENCE_MODULE[module as CTFModuleTypes];
    const moduleGroups = subgrouped.moduleUnits.get(moduleKey) ?? [];
    return moduleGroups
      .filter((group) => unfilteredIds.has(group.representative.item.id))
      .map((group) => group.representative.item);
  },
);

// difference groups which are ordered, not filtered and excluded to be displayed on discarded results
export const getDiscardedGroups = createSelector(
  [getUnfilteredDifferences, getSelectedModule],
  (differences, module): Difference[] => {
    const alreadyGrouped: Array<string> = [];

    const groupedDiffs = differences.filter((difference) => {
      // we need to keep track of the groups we already included in the differences list.
      if (difference.groupId) {
        if (alreadyGrouped.includes(difference.groupId)) {
          return false;
        }
        alreadyGrouped.push(difference.groupId);
      }

      return difference;
    });

    return filterDifferencesByModule(groupedDiffs, module);
  },
);

// differences which are not filtered and excluded
export const getDiscardedDifferences = createSelector(
  [getUnfilteredDifferences, getSelectedModule],
  (differences, module) => {
    const excludedDiffs = differences.filter((difference) => difference.smartDiscard);

    return filterDifferencesByModule(excludedDiffs, module);
  },
);

// Filters differences down to the given module tab. Markups live in their own dedicated tab (plus
// the All tab), so they are excluded from every other module. getHidden returns the inverse set.
export function filterDifferencesByModule(
  differences: Difference[],
  module: CTFModuleTypes | 'All',
  getHidden = false, // for getting what differences are hidden by the module
) {
  if (module === 'All') {
    return getHidden ? [] : differences;
  }

  if (module === CTFModuleTypes.text) {
    const nonTextModuleTypes = [
      DifferenceTypeNames.Barcode,
      DifferenceTypeNames.Braille,
      DifferenceTypeNames.Graphics,
      DifferenceTypeNames.SpellingGeneral,
      DifferenceTypeNames.Abbreviation,
      DifferenceTypeNames.ProperNoun,
      DifferenceTypeNames.Alphanumeric,
      DifferenceTypeNames.FontMetrics,
      DifferenceTypeNames.Color,
      DifferenceTypeNames.Markup,
    ];
    return differences.filter((diff) =>
      getHidden ? nonTextModuleTypes.includes(diff.type) : !nonTextModuleTypes.includes(diff.type),
    );
  } else if (module === CTFModuleTypes.spelling) {
    const spellingModuleTypes = [
      DifferenceTypeNames.SpellingGeneral,
      DifferenceTypeNames.Abbreviation,
      DifferenceTypeNames.ProperNoun,
      DifferenceTypeNames.Alphanumeric,
    ];
    return differences.filter((diff) =>
      getHidden ? !spellingModuleTypes.includes(diff.type) : spellingModuleTypes.includes(diff.type),
    );
  }

  const moduleTypeMapping: Partial<Record<CTFModuleTypes, DifferenceTypeNames[]>> = {
    [CTFModuleTypes.barcode]: [DifferenceTypeNames.Barcode],
    [CTFModuleTypes.braille]: [DifferenceTypeNames.Braille],
    [CTFModuleTypes.graphics]: [DifferenceTypeNames.Graphics],
    [CTFModuleTypes.fontMetrics]: [DifferenceTypeNames.FontMetrics],
    [CTFModuleTypes.color]: [DifferenceTypeNames.Color],
    [CTFModuleTypes.markup]: [DifferenceTypeNames.Markup],
  };
  const filterTypes = moduleTypeMapping[module];
  if (!filterTypes) {
    return differences.filter((diff) =>
      getHidden ? diff.type === DifferenceTypeNames.Markup : diff.type !== DifferenceTypeNames.Markup,
    );
  }

  return differences.filter((diff) => (getHidden ? !filterTypes.includes(diff.type) : filterTypes.includes(diff.type)));
}

// differences which are not filtered out AND are in the selected module
export const getDisplayedDifferences = createSelector(
  [getUnfilteredDifferences, getSelectedModule],
  (differences, module): Difference[] => {
    return filterDifferencesByModule(differences, module);
  },
);

// differences which are filtered out OR not in the selected module
export const getHiddenDifferences = createSelector(
  [getUnfilteredDifferences, getFilteredDifferences, getSelectedModule],
  (unfilteredDifferences, filteredDifferences, module) => {
    const differencesNotInModuleSet = new Set(filterDifferencesByModule(unfilteredDifferences, module, true));
    return [...differencesNotInModuleSet, ...filteredDifferences];
  },
);

export const getDisplayedAndHiddenDifferences = createSelector(
  [getDifferences, getDifferenceTypeFilters, getDifferenceStatusFilters, getSelectedModule],
  (differences, typeFilters, statusFilters, module) => {
    const moduleFilteredSet = new Set(filterDifferencesByModule(differences, module));

    return differences.reduce(
      (acc, diff) => {
        const isFiltered = isFilteredOut(diff, typeFilters, statusFilters);
        const isInModule = moduleFilteredSet.has(diff);

        if (!isFiltered && isInModule) {
          acc.displayed.push(diff);
        } else {
          acc.hidden.push(diff);
        }

        return acc;
      },
      { displayed: [], hidden: [] } as { displayed: Difference[]; hidden: Difference[] },
    );
  },
);

export const getSmartDiscardQty = createSelector([getDifferences], (differences) => {
  return differences.filter((difference) => difference.smartDiscard).length;
});

// Count of smart-discarded differences that the currently selected module would
// actually include in its visible set if the Smart Discards filter were toggled
// on. Used by the filter panel to hide the Smart Discards row on modules where
// no discarded cards exist to surface (so the user is never shown a toggle that
// does nothing). Derived from `filterDifferencesByModule` so it stays correct
// as that function's module rules evolve.
export const getModuleSmartDiscardQty = createSelector([getDifferences, getSelectedModule], (differences, module) => {
  const discarded = differences.filter((difference) => difference.smartDiscard);
  return filterDifferencesByModule(discarded, module).length;
});

// barcode differences
export const getBarcodeDifferencesExist = createSelector([getDifferences], (differences) => {
  const barcodelist = differences.filter((difference) => difference.type === DifferenceTypeNames.Barcode);
  return barcodelist.length > 0;
});

// braille differences
export const getBrailleDifferencesExist = createSelector([getDifferences], (differences) => {
  return differences.some((difference) => difference.type === DifferenceTypeNames.Braille);
});

/* SEGMENT/AUDIT */
export const getSegmentDisplayedDifferenceCounts = createSelector([getDifferences], (differences) => {
  return getTypeCounts(differences);
});

export const getSegmentCommentedDifferenceCounts = createSelector([getCommentedDifferences], (differences) => {
  return getTypeCounts(differences);
});

export const getSmartDiscarded = createSelector([getDiscardedDifferences], (discardedDifferences) => {
  return discardedDifferences.some((difference) => difference.smartDiscard);
});

export const hasLegacyExcludedDifferences = createSelector([getDifferences], (differences) =>
  differences.some((difference) => difference.excluded),
);
